import { put, takeEvery, all, call, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import {
  addLoadingClient,
  removeLoadingClient,
  getClientsSuccess,
  getClientSuccess,
  clientsError,
  createClientSuccess,
  updateClientSuccess,
  deleteClientSuccess,
  updateUsers,
  getClient,
  getClientAuditLogsSuccess,
} from '../actions/clients';

import {
  SET_CAPUROLE,
  ADD_SUBSCRIPTION_CLIENT,
  GET_CLIENTS,
  GET_CLIENT,
  ADD_CLIENT,
  CANCEL_UPDATE,
  CANCEL_CREATE,
  SELECT_CLIENT,
  CREATE_CLIENT,
  UPDATE_CLIENT,
  DELETE_CLIENT,
  REMOVE_SUBSCRIPTION_CLIENT,
  CREATE_CLIENT_WHITELABEL,
  DELETE_CLIENT_WHITELABEL,
  GET_CLIENT_AUDIT_LOGS,
} from '../constants/action-types/clients';

import { getUser, refreshUserInfo } from '../actions/users';

import { makeSelectIdTkn } from '../selectors/auth';
import { makeSelectClient, makeSelectSubscription } from '../selectors/clients';
import clientApi from '../utils/clientApi';
import subscriptionApi from '../utils/subscriptionApi';

// Utils
function* createHeader(opts = {}) {
  const idToken = yield select(makeSelectIdTkn());
  const options = JSON.parse(JSON.stringify(opts));
  options.headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`,
  };
  return options;
}

// Workers
export function* getClients() {
  try {
    const options = yield call(createHeader);
    const res = yield call(clientApi.get, '/clients?include=businessLines', options);
    yield put(getClientsSuccess(res.data));
  } catch (error) {
    yield put(clientsError());
    yield put(push('/administration/clients'));
  }
}

export function* getClientInfo(action) {
  try {
    const options = yield call(createHeader);
    const res = yield call(clientApi.get, `/clients/${action.id}`, options);
    const clientData = res.data;
    yield put(getClientSuccess(clientData));
  } catch (error) {
    yield put(clientsError());
    yield put(push('/administration/clients'));
  }
}

function* createClient(action) {
  try {
    // Create in client services
    const options = yield call(createHeader);
    const res = yield call(clientApi.post, '/clients', action.data, options);
    const clientData = res.data;
    yield put(createClientSuccess(clientData));
    yield put(push(`/administration/clients/${res.data.id}`));
    // Refresh the redux store from server
    yield put(refreshUserInfo());
  } catch (error) {
    yield put(clientsError());
    yield put(push('/administration/clients'));
  }
}

function* updateClient(action) {
  try {
    const options = yield call(createHeader);
    const res = yield call(clientApi.put, `/clients/${action.id}`, action.data, options);
    const clientData = res.data;
    yield put(updateClientSuccess(clientData));
  } catch (error) {
    yield put(clientsError());
    yield put(push('/administration/clients'));
  }
}

function* deleteClient(action) {
  try {
    // Delete in client services
    const options = yield call(createHeader);
    yield call(clientApi.delete, `/clients/${action.id}`, options);
    yield put(deleteClientSuccess());
    // Refresh the redux store from server
    yield put(refreshUserInfo());
  } catch (error) {
    yield put(clientsError());
  }
  yield put(push('/administration/clients'));
}

function* getClientAuditLogs(action) {
  try {
    const options = yield call(createHeader);
    const res = yield call(clientApi.get, `/clients/${action.id}/audit-logs`, options);
    yield put(getClientAuditLogsSuccess(res.data));
  } catch (error) {
    yield put(clientsError());
  }
}

function* createClientWhitelabel(action) {
  try {
    const options = yield call(createHeader);
    options.headers['Content-Type'] = 'multipart/form-data';
    const formData = new FormData();
    formData.append('id', action.id);
    formData.append('logoFile', action.data);
    yield call(clientApi.post, `/labels/client/`, formData, options);
    yield put(getClient(action.id));
  } catch (error) {
    yield put(clientsError());
  }
}

function* deleteClientWhitelabel(action) {
  try {
    const options = yield call(createHeader);
    yield call(clientApi.delete, `/labels/${action.id}`, options);
    yield put(getClient(action.clientId));
  } catch (error) {
    yield put(clientsError());
  }
}

// Subscription Calls
function* SubscribeToApplication(action) {
  try {
    const client = yield select(makeSelectClient());
    const sub = yield select(makeSelectSubscription());

    const reqSuf = `/subscription/${sub}/client/${client.id}`;

    const options = yield call(createHeader);
    yield call(clientApi.post, reqSuf, { tier: action.tier }, options);
    yield put(getClient(client.id));
  } catch (e) {
    yield put(clientsError());
  }
}

function* UnSubscribeFromApplication() {
  try {
    const client = yield select(makeSelectClient());
    const sub = yield select(makeSelectSubscription());

    const reqSuf = `/subscription/${sub}/client/${client.id}`;

    const options = yield call(createHeader);
    yield call(clientApi.delete, reqSuf, options);
    yield put(getClient(client.id));
  } catch (e) {
    yield put(clientsError());
  }
}

// Get a client's current subscription information
function* GetUsers() {
  const reqSuf = `/users`;
  const options = yield call(createHeader);
  const resp = yield call(subscriptionApi.get, reqSuf, options);
  yield put(updateUsers(resp.data.message));
}

function* SwitchClientAppUserRole(action) {
  yield put(addLoadingClient(SET_CAPUROLE));
  try {
    const { uid, cid, app, role } = action.roleParams;
    const reqSuf = `/client/${cid}/user/${uid}/app/${app}/role/${role}`;

    const options = yield call(createHeader);
    yield call(subscriptionApi.patch, reqSuf, {}, options);

    yield put(removeLoadingClient(SET_CAPUROLE));
    yield GetUsers();
    yield put(getUser(uid));
  } catch (e) {
    // TODO: ivan.santos - report error to sentry
    // console.log('err', e);
  }
}

// Routes
function* redirectToClients() {
  yield put(push('/administration/clients'));
}

function* addClient() {
  yield put(push('/administration/clients/create'));
}

function* redirectToSelectedClient(action) {
  yield put(push(`/administration/clients/${action.id}`));
}

// Watchers
function* watchGetClients() {
  yield takeEvery(GET_CLIENTS, getClients);
}

function* watchRedirectClients() {
  yield takeEvery(CANCEL_CREATE, redirectToClients);
}

function* watchRedirectSelectedClient() {
  yield takeEvery(SELECT_CLIENT, redirectToSelectedClient);
  yield takeEvery(CANCEL_UPDATE, redirectToSelectedClient);
}

function* watchGetClient() {
  yield takeEvery(GET_CLIENT, getClientInfo);
}

function* watchCreateClient() {
  yield takeEvery(CREATE_CLIENT, createClient);
}

function* watchAddClient() {
  yield takeEvery(ADD_CLIENT, addClient);
}

function* watchUpdateClient() {
  yield takeEvery(UPDATE_CLIENT, updateClient);
}

function* watchDeleteClient() {
  yield takeEvery(DELETE_CLIENT, deleteClient);
}

function* watchGetClientAuditLogs() {
  yield takeEvery(GET_CLIENT_AUDIT_LOGS, getClientAuditLogs);
}

function* watchCreateClientWhitelabel() {
  yield takeEvery(CREATE_CLIENT_WHITELABEL, createClientWhitelabel);
}

function* watchDeleteClientWhitelabel() {
  yield takeEvery(DELETE_CLIENT_WHITELABEL, deleteClientWhitelabel);
}

function* watchCreateClientSubscription() {
  yield takeEvery(ADD_SUBSCRIPTION_CLIENT, SubscribeToApplication);
}

function* watchDeleteClientSubscription() {
  yield takeEvery(REMOVE_SUBSCRIPTION_CLIENT, UnSubscribeFromApplication);
}

function* watchSetUserSubscriptionRole() {
  yield takeEvery(SET_CAPUROLE, SwitchClientAppUserRole);
}

export default function* clientsSaga() {
  yield all([
    watchGetClients(),
    watchRedirectClients(),
    watchRedirectSelectedClient(),
    watchGetClient(),
    watchCreateClient(),
    watchAddClient(),
    watchUpdateClient(),
    watchDeleteClient(),
    watchGetClientAuditLogs(),
    watchCreateClientWhitelabel(),
    watchDeleteClientWhitelabel(),
    watchCreateClientSubscription(),
    watchDeleteClientSubscription(),
    watchSetUserSubscriptionRole(),
  ]);
}
